import { useState, useEffect, useCallback, useRef } from 'react';
import { analytics, CHAT_ANALYTICS_EVENTS } from '../services/analytics';

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  type?: 'text' | 'assets' | 'qc-report' | 'trace';
  data?: any;
  assets?: any[]; // Store assets if merged with text
}

export interface DebugEvent {
  type: 'debug';
  event: string;
  name: string;
  node?: string;
  input?: any;
  output?: any;
  content?: string;
  timestamp?: string;
}

export interface ChatAnalyticsContext {
  accountId?: string | null;
  workspaceId?: string | null;
  agentSlug?: string | null;
  selectedSessionId?: string | null;
}

interface PendingTurn {
  startedAt: number;
  toolNames: string[];
  activeToolCount: number;
  assistantChars: number;
  sawOutput: boolean;
  completionTimer?: ReturnType<typeof setTimeout>;
  watchdogTimer?: ReturnType<typeof setTimeout>;
}

type TurnOutcome = 'completed' | 'interrupted' | 'error';

const TURN_COMPLETE_DEBOUNCE_MS = 1200;
// Grace period after a tool finishes: the reasoner may take several seconds to
// emit its first response token, and any token resets this to the short debounce.
const TURN_IDLE_AFTER_TOOL_MS = 15000;
// Hard ceiling so a turn that never produces a terminal signal is recorded as
// interrupted rather than leaving the input disabled forever.
const TURN_WATCHDOG_MS = 180000;
// GA4 custom event parameters must be scalars, so tool names are serialized
// into a single bounded string rather than sent as an array.
const MAX_TRACKED_TOOL_NAMES = 10;
const MAX_TOOL_NAMES_LENGTH = 100;

function serializeToolNames(names: string[]): string {
  let serialized = '';
  for (const name of names.slice(0, MAX_TRACKED_TOOL_NAMES)) {
    const next = serialized ? `${serialized},${name}` : name;
    if (next.length > MAX_TOOL_NAMES_LENGTH) break;
    serialized = next;
  }
  return serialized;
}

function getThinkingLabel(event: DebugEvent): string | null {
  const name = event.name || '';
  if (event.event === 'node_start') {
    if (name === 'reasoner') return '🧠 Reasoning...';
    if (name === 'tools') return '⚡ Running tools...';
    if (name === 'AgentRouter') return '🔀 Routing...';
  }
  if (event.event === 'tool_start') {
    if (name === 'search_tool') return '🔍 Searching...';
    if (name === 'knowledge_tool') return '📚 Looking up knowledge...';
    if (name === 'tag_cloud_tool') return '🏷️ Discovering tags...';
    if (name === 'account_tool') return '🏢 Checking account...';
    if (name === 'qc_tool') return '✅ Running QC...';
    return `🔧 Running ${name}...`;
  }
  if (event.event === 'tool_end') return '🧠 Reasoning...';
  return null;
}

function analyticsContextParams(
  context: ChatAnalyticsContext,
  sessionId?: string | null
): Record<string, string | undefined> {
  return {
    account_id: context.accountId || undefined,
    workspace_id: context.workspaceId || undefined,
    // The backend chat session is reported separately; `app_session_id` is owned
    // by the analytics service so events stay joinable across the whole app session.
    conversation_id: sessionId || context.selectedSessionId || undefined,
  };
}

function extractQcMetadata(input: unknown): { asset_id?: string; qc_type?: string } {
  if (!input || typeof input !== 'object') return {};
  const data = input as Record<string, unknown>;
  return {
    asset_id: typeof data.asset_id === 'string' ? data.asset_id : undefined,
    qc_type: typeof data.qc_type === 'string' ? data.qc_type : undefined,
  };
}

function parseToolOutput(output: unknown): unknown {
  const content = output && typeof output === 'object' && 'content' in output
    ? (output as { content?: unknown }).content
    : output;

  if (typeof content !== 'string') return content;
  try {
    return JSON.parse(content);
  } catch {
    return content;
  }
}

function summarizeToolResult(output: unknown): Record<string, unknown> {
  const parsed = parseToolOutput(output);
  if (!parsed || typeof parsed !== 'object') {
    return {};
  }

  const data = parsed as Record<string, unknown>;
  const items = Array.isArray(data.items) ? data.items : undefined;
  return {
    result_count: typeof data.result_count === 'number'
      ? data.result_count
      : typeof data.count === 'number'
        ? data.count
        : items?.length,
    status: typeof data.status === 'string' ? data.status : undefined,
    stage_used: typeof data.stage_used === 'string' ? data.stage_used : undefined,
  };
}

function isToolResultSuccess(output: unknown): boolean {
  const parsed = parseToolOutput(output);
  if (typeof parsed === 'string') {
    return !/^error[:\s]/i.test(parsed.trim());
  }
  if (parsed && typeof parsed === 'object') {
    const data = parsed as Record<string, unknown>;
    if (data.error || data.error_code) return false;
    if (typeof data.status === 'string' && data.status.toLowerCase() === 'error') return false;
  }
  return true;
}

const useChat = (
  url: string,
  onSessionIdReceived?: (id: string) => void,
  analyticsContext: ChatAnalyticsContext = {}
) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [debugEvents, setDebugEvents] = useState<DebugEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState<string | null>(null);
  const [suggestedSearches, setSuggestedSearches] = useState<string[]>([]);
  const [thinkingStatus, setThinkingStatus] = useState<string | null>(null);
  const [isResponding, setIsResponding] = useState(false);
  const ws = useRef<WebSocket | null>(null);
  const authenticated = useRef(false);
  const contextRef = useRef<ChatAnalyticsContext>(analyticsContext);
  const messagesRef = useRef<Message[]>([]);
  const currentSessionId = useRef<string | null>(null);
  const sessionStartedTracked = useRef(false);
  const pendingTurn = useRef<PendingTurn | null>(null);

  useEffect(() => {
    contextRef.current = analyticsContext;
  }, [analyticsContext]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const finalizePendingTurn = useCallback((outcome: TurnOutcome, errorType?: string) => {
    const turn = pendingTurn.current;
    pendingTurn.current = null;
    setIsResponding(false);
    if (!turn) return;
    if (turn.completionTimer) clearTimeout(turn.completionTimer);
    if (turn.watchdogTimer) clearTimeout(turn.watchdogTimer);

    const success = outcome === 'completed';
    analytics.track(CHAT_ANALYTICS_EVENTS.TURN_COMPLETED, {
      ...analyticsContextParams(contextRef.current, currentSessionId.current),
      success,
      outcome,
      duration_ms: Math.round(performance.now() - turn.startedAt),
      tool_call_count: turn.toolNames.length,
      tool_names: serializeToolNames(turn.toolNames),
      final_response_length: success ? turn.assistantChars : 0,
      error_type: errorType || null,
    });
  }, []);

  /**
   * Arms the completion debounce. Never fires while a tool is executing, so a
   * tool that outlives the debounce can no longer complete the turn early.
   */
  const scheduleTurnCompleted = useCallback((delayMs: number = TURN_COMPLETE_DEBOUNCE_MS) => {
    const turn = pendingTurn.current;
    if (!turn) return;
    if (turn.completionTimer) clearTimeout(turn.completionTimer);
    turn.completionTimer = undefined;
    if (turn.activeToolCount > 0) return;
    turn.completionTimer = setTimeout(
      () => finalizePendingTurn('completed'),
      delayMs
    );
  }, [finalizePendingTurn]);

  const noteTurnOutput = useCallback((chars: number) => {
    const turn = pendingTurn.current;
    if (!turn) return;
    turn.assistantChars += chars;
    turn.sawOutput = true;
    scheduleTurnCompleted();
  }, [scheduleTurnCompleted]);

  const noteToolStarted = useCallback((toolName: string) => {
    const turn = pendingTurn.current;
    if (!turn) return;
    if (!turn.toolNames.includes(toolName)) {
      turn.toolNames.push(toolName);
    }
    turn.activeToolCount += 1;
    if (turn.completionTimer) {
      clearTimeout(turn.completionTimer);
      turn.completionTimer = undefined;
    }
  }, []);

  const noteToolFinished = useCallback(() => {
    const turn = pendingTurn.current;
    if (!turn) return;
    turn.activeToolCount = Math.max(0, turn.activeToolCount - 1);
    if (turn.activeToolCount === 0 && turn.sawOutput) {
      scheduleTurnCompleted(TURN_IDLE_AFTER_TOOL_MS);
    }
  }, [scheduleTurnCompleted]);

  const trackSessionStarted = useCallback((sessionId: string | undefined, historyMessageCount: number) => {
    if (!sessionId || sessionStartedTracked.current) return;
    currentSessionId.current = sessionId;
    sessionStartedTracked.current = true;
    analytics.track(CHAT_ANALYTICS_EVENTS.SESSION_STARTED, {
      ...analyticsContextParams(contextRef.current, sessionId),
      is_new_session: !contextRef.current.selectedSessionId,
      history_message_count: historyMessageCount,
    });
  }, []);

  useEffect(() => {
    finalizePendingTurn('interrupted', 'session_changed');
    setMessages([]); // Clear messages when URL changes (new session)
    setDebugEvents([]);
    setWelcomeMessage(null);
    setSuggestedSearches([]);
    setThinkingStatus(null);
    authenticated.current = false;
    currentSessionId.current = null;
    sessionStartedTracked.current = false;

    // Do not attempt to connect until we have a fully formed WebSocket URL.
    if (!url) {
      setIsConnected(false);
      setError(null);
      ws.current = null;
      return;
    }

    let isUnmounted = false;

    try {
      ws.current = new WebSocket(url);

      ws.current.onopen = () => {
        if (isUnmounted) return;
        // Send token as first message (not in URL to avoid log leakage)
        const token = sessionStorage.getItem('artstash_token');
        if (token && ws.current) {
          ws.current.send(JSON.stringify({ type: 'auth', token }));
        } else {
          setError('No auth token — please log in');
          ws.current?.close();
        }
      };

      ws.current.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);

          // Handle auth handshake response
          if (parsed.type === 'auth_ok') {
            authenticated.current = true;
            if (!isUnmounted) {
              setIsConnected(true);
              setError(null);
            }
            return;
          }
          if (parsed.type === 'auth_error') {
            if (!isUnmounted) {
              setError(parsed.content || 'Authentication failed');
              setIsConnected(false);
            }
            analytics.track(CHAT_ANALYTICS_EVENTS.ERROR_OCCURRED, {
              ...analyticsContextParams(contextRef.current, currentSessionId.current),
              error_type: 'auth_error',
              where: 'websocket_auth',
            });
            return;
          }

          if (parsed.type === 'session_init') {
            currentSessionId.current = parsed.sessionId || null;
            trackSessionStarted(parsed.sessionId, 0);
            if (onSessionIdReceived) onSessionIdReceived(parsed.sessionId);
            if (parsed.welcomeMessage) setWelcomeMessage(parsed.welcomeMessage);
            if (parsed.suggestedSearches) setSuggestedSearches(parsed.suggestedSearches);
            return;
          }

          if (parsed.type === 'debug') {
            setDebugEvents((prev) => [...prev, parsed]);
            if (parsed.event === 'tool_start' && parsed.name) {
              noteToolStarted(parsed.name);
              if (parsed.name === 'qc_tool') {
                analytics.track(CHAT_ANALYTICS_EVENTS.QC_INVOKED, {
                  ...analyticsContextParams(contextRef.current, currentSessionId.current),
                  ...extractQcMetadata(parsed.input),
                });
              }
            }
            if (parsed.event === 'tool_end' && parsed.name) {
              noteToolFinished();
              const success = isToolResultSuccess(parsed.output);
              const resultSummary = summarizeToolResult(parsed.output);
              const commonParams = analyticsContextParams(contextRef.current, currentSessionId.current);

              analytics.track(CHAT_ANALYTICS_EVENTS.TOOL_INVOKED, {
                ...commonParams,
                tool_name: parsed.name,
                success,
                ...resultSummary,
              });

              if (parsed.name === 'search_tool') {
                analytics.track(CHAT_ANALYTICS_EVENTS.SEARCH_EXECUTED, {
                  ...commonParams,
                  success,
                  ...resultSummary,
                });
              }
              if (parsed.name === 'knowledge_tool') {
                analytics.track(CHAT_ANALYTICS_EVENTS.KNOWLEDGE_RESOLVED, {
                  ...commonParams,
                  success,
                  entities_found: resultSummary.result_count,
                });
              }
            }
            const label = getThinkingLabel(parsed as DebugEvent);
            if (label) setThinkingStatus(label);
            return;
          }

          if (parsed.type === 'history') {
            // Merge asset data into the preceding text message so inline
            // images render the same way they do during live streaming.
            const raw: Message[] = parsed.messages || [];
            const merged: Message[] = [];
            for (const m of raw) {
              if (m.type === 'assets' && m.data && merged.length > 0) {
                const prev = merged[merged.length - 1];
                if (prev.role === 'assistant' && prev.type === 'text') {
                  prev.assets = [...(prev.assets || []), ...m.data];
                }
              }
              merged.push(m);
            }
            setMessages(merged);
            if (parsed.sessionId) {
                currentSessionId.current = parsed.sessionId;
                trackSessionStarted(parsed.sessionId, raw.length);
                if (onSessionIdReceived) onSessionIdReceived(parsed.sessionId);
            }
            return;
          }

          if (parsed.type === 'token') {
            setThinkingStatus(null);
            noteTurnOutput((parsed.content || '').length);
            // Streaming token - append to existing text message or create new one
            setMessages((prev) => {
              const lastMsg = prev[prev.length - 1];

              if (lastMsg && lastMsg.role === 'assistant' && lastMsg.type === 'text') {
                // Append to existing text message
                const newContent = lastMsg.content + (parsed.content || '');
                return [...prev.slice(0, -1), { ...lastMsg, content: newContent }];
              }

              // New text message (even if last was 'assets' — don't overwrite it)
              return [...prev, { role: 'assistant', content: parsed.content || '', type: 'text' }];
            });
            return;
          }

          if (parsed.type === 'error') {
            const content = parsed.content || 'Unknown chat error';
            setError(content);
            setThinkingStatus(null);
            analytics.track(CHAT_ANALYTICS_EVENTS.ERROR_OCCURRED, {
              ...analyticsContextParams(contextRef.current, currentSessionId.current),
              error_type: 'chat_error',
              where: 'websocket_loop',
            });
            finalizePendingTurn('error', 'chat_error');
            return;
          }

          let msg: Message = {
            role: 'assistant',
            content: parsed.content || ''
          };

          if (parsed.type === 'assets') {
             noteTurnOutput(0);
             // Attach assets to the preceding text message so inline images can render
             setMessages((prev) => {
               const lastTextIdx = [...prev].reverse().findIndex(
                 (m) => m.role === 'assistant' && m.type === 'text'
               );
               if (lastTextIdx !== -1) {
                 const idx = prev.length - 1 - lastTextIdx;
                 const updated = { ...prev[idx], assets: parsed.items };
                 return [...prev.slice(0, idx), updated, ...prev.slice(idx + 1)];
               }
               // No preceding text — show as standalone asset grid
               return [...prev, { role: 'assistant', content: 'Found assets:', type: 'assets', data: parsed.items }];
             });
             return;
           } else if (parsed.type === 'qc-report') {
             msg.type = 'qc-report';
             msg.data = parsed.data || parsed;
             noteTurnOutput(0);
          } else if (parsed.type === 'text') {
             msg.type = 'text';
             msg.content = parsed.content;
             noteTurnOutput((parsed.content || '').length);
          } else if (parsed.type === 'trace') {
             msg.type = 'trace';
             msg.content = parsed.content;
          } else {
             // Fallback
             msg.content = JSON.stringify(parsed);
          }

          setMessages((prev) => [...prev, msg]);
        } catch {
          setMessages((prev) => [...prev, { role: 'assistant', content: event.data, type: 'text' }]);
          noteTurnOutput(String(event.data || '').length);
        }
      };

      ws.current.onerror = (event) => {
        if (isUnmounted) return;
        console.error("WebSocket error:", event);
        setError("Connection error");
        setIsConnected(false);
        setThinkingStatus(null);
        analytics.track(CHAT_ANALYTICS_EVENTS.ERROR_OCCURRED, {
          ...analyticsContextParams(contextRef.current, currentSessionId.current),
          error_type: 'websocket_error',
          where: 'streaming',
        });
        finalizePendingTurn('error', 'websocket_error');
      };

      ws.current.onclose = () => {
        if (isUnmounted) return;
        setIsConnected(false);
        setThinkingStatus(null);
        finalizePendingTurn('interrupted', 'connection_closed');
      };
    } catch (e) {
      console.error("WebSocket connection failed:", e);
      setError("Failed to connect");
      analytics.track(CHAT_ANALYTICS_EVENTS.ERROR_OCCURRED, {
        ...analyticsContextParams(contextRef.current, currentSessionId.current),
        error_type: e instanceof Error ? e.name : 'websocket_connect_error',
        where: 'websocket_connect',
      });
    }

    return () => {
      isUnmounted = true;

      if (ws.current) {
        const socket = ws.current;
        const STATE_CONNECTING = 0;
        const STATE_OPEN = 1;
        ws.current.onopen = null;
        ws.current.onmessage = null;
        ws.current.onerror = null;
        ws.current.onclose = null;

        if (socket.readyState === STATE_CONNECTING && typeof socket.addEventListener === 'function') {
          // In React StrictMode dev remounts, close after open to avoid noisy browser warnings.
          socket.addEventListener('open', () => socket.close(), { once: true });
        } else if (socket.readyState === STATE_OPEN) {
          socket.close();
        }

        ws.current = null;
      }
      // Cleanup can run because the account/session URL changed or because the
      // component unmounted mid-response. Either way no completion signal was
      // observed, so the turn must not be reported as successful.
      finalizePendingTurn('interrupted', 'connection_closed');
    };
  }, [url, onSessionIdReceived, finalizePendingTurn, noteTurnOutput, noteToolStarted, noteToolFinished, trackSessionStarted]);

  const sendMessage = useCallback((content: string) => {
    if (pendingTurn.current) {
      // A turn is still in flight. Submitting now would misattribute its
      // remaining output to the new turn, so the send is refused instead.
      return;
    }
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      const turn: PendingTurn = {
        startedAt: performance.now(),
        toolNames: [],
        activeToolCount: 0,
        assistantChars: 0,
        sawOutput: false,
      };
      turn.watchdogTimer = setTimeout(
        () => finalizePendingTurn('interrupted', 'turn_timeout'),
        TURN_WATCHDOG_MS
      );
      pendingTurn.current = turn;
      setIsResponding(true);
      analytics.track(CHAT_ANALYTICS_EVENTS.QUERY_SUBMITTED, {
        ...analyticsContextParams(contextRef.current, currentSessionId.current),
        query_length: content.length,
        history_message_count: messagesRef.current.length,
      });
      // Send fresh token with each message so backend has a valid token for DAM API calls
      const token = sessionStorage.getItem('artstash_token');
      ws.current.send(JSON.stringify({ type: 'message', content, token }));
      setMessages((prev) => [...prev, { role: 'user', content, type: 'text' }]);
      setThinkingStatus('🧠 Thinking...');
    } else {
      setError("Not connected");
      analytics.track(CHAT_ANALYTICS_EVENTS.ERROR_OCCURRED, {
        ...analyticsContextParams(contextRef.current, currentSessionId.current),
        error_type: 'not_connected',
        where: 'send_message',
      });
    }
  }, [finalizePendingTurn]);

  const clearDebug = useCallback(() => setDebugEvents([]), []);

  const sendRaw = useCallback((payload: object) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(payload));
    }
  }, []);

  return { messages, sendMessage, error, isConnected, isResponding, debugEvents, clearDebug, sendRaw, welcomeMessage, suggestedSearches, thinkingStatus };
};

export default useChat;
